import { useState } from 'react'
import { PlusIcon, DotsSixVerticalIcon } from '@phosphor-icons/react'
import { propExistsOnObject, renderColorPicker } from '../../utils'
import { DragDropContext, Draggable } from 'react-beautiful-dnd'
import { ScoopTheme } from '../../../../../../Insights/Style'
import { StrictModeDroppable } from '../../../../../../Objects/Frame/FrameDrawer'
import { cloneDeep } from 'lodash'
import { ScoopSwitch, ScoopText, ScoopRow, ScoopColumn } from '../../../../../../ui'
import styles from './ColorConfig.module.scss'

export const ColorConfig = ({ config, setConfig, theme, server }) => {
    const [isDarkTheme, setIsDarkTheme] = useState(theme?.colorScheme.darkTheme)

    const getBackgroundColor = () => {
        if (propExistsOnObject(config.styleOverrides, 'backgroundColor'))
            return config.styleOverrides.backgroundColor
        if (theme?.colorScheme) return theme.colorScheme.backgroundColor
        return ScoopTheme.backgroundColor
    }

    const getColorPalette = () => {
        if (propExistsOnObject(config.styleOverrides, 'color')) return config.styleOverrides.color
        if (theme?.colorScheme) return theme.colorScheme.colors.map((c) => c.val)
        return ScoopTheme.color
    }

    const handleBackgroundColorChange = (color) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.backgroundColor = color
        setConfig(newConfig)
    }

    const handlePaletteChange = (newColor, index) => {
        const newPalette = [...getColorPalette()]
        newPalette[index] = newColor
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.color = newPalette
        setConfig(newConfig)
    }

    const onDragEnd = (result) => {
        if (!result.destination) return

        const newPalette = Array.from(getColorPalette())
        const [moved] = newPalette.splice(result.source.index, 1)
        newPalette.splice(result.destination.index, 0, moved)
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.color = newPalette
        setConfig(newConfig)
    }

    const toggleTheme = () => {
        server.postData(
            {
                action: 'putObject',
                class: 'scoop.presentation.Theme',
                value: {
                    themeID: theme.themeID,
                    canvasID: theme.canvasID,
                    themeName: theme.themeName,
                    fonts: theme.fonts,
                    chartPreferences: theme.chartPreferences,
                    colorScheme: {
                        ...theme.colorScheme,
                        darkTheme: !isDarkTheme,
                    },
                },
            },
            () => setIsDarkTheme(!isDarkTheme)
        )
    }

    return (
        <>
            <ScoopColumn className={styles.container}>
                {theme && (
                    <ScoopRow className={styles.switchRow}>
                        <ScoopSwitch checked={isDarkTheme} onChange={() => toggleTheme()} />
                        <ScoopText className={styles.label}>Dark theme</ScoopText>
                    </ScoopRow>
                )}
            </ScoopColumn>
            <ScoopColumn className={styles.backgroundSection}>
                {renderColorPicker(
                    'Background color',
                    getBackgroundColor(),
                    handleBackgroundColorChange
                )}
            </ScoopColumn>
            {config.view === 'chart' && (
                <ScoopColumn className={styles.chartColorsSection}>
                    <ScoopRow className={styles.chartColorsHeader}>
                        <ScoopText className={styles.label}>Chart colors</ScoopText>
                        <PlusIcon size={18} className={styles.addIcon} />
                    </ScoopRow>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <StrictModeDroppable droppableId="droppable">
                            {(provided) => (
                                <ScoopColumn
                                    className={styles.colorList}
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {getColorPalette().map((color, index) => (
                                        <Draggable
                                            key={color}
                                            draggableId={'draggable-' + color}
                                            index={index}
                                        >
                                            {(provided) => (
                                                <ScoopRow
                                                    className={styles.colorItem}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <DotsSixVerticalIcon
                                                        size={16}
                                                        className={styles.dragHandle}
                                                    />
                                                    {renderColorPicker(false, color, (newColor) =>
                                                        handlePaletteChange(newColor, index)
                                                    )}
                                                </ScoopRow>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </ScoopColumn>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                </ScoopColumn>
            )}
        </>
    )
}
